// ** React Imports
import { useEffect, useState } from 'react'

// ** Next Imports

// ** MUI Components
import Box from '@mui/material/Box'
import Card from '@mui/material/Card'
import Grid from '@mui/material/Grid'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'
import Alert from '@mui/material/Alert'
import { Button, Divider, Select, MenuItem } from '@mui/material'

// ** MUI Theme and Styling
import { styled, useTheme } from '@mui/material/styles'

// ** Icon Imports
import Icon from 'src/@core/components/icon'

// ** Third Party Imports
import toast from 'react-hot-toast'

// ** Custom Components
import CustomHeader from 'src/@core/components/Header'
import { construirEnlace, leerEstadoDeUrl } from 'src/@core/utils/enlace-compartible'

/*
  Duracion de cada escudo. Estaba dentro de handleSaveData; se saca porque ahora la
  necesitan dos sitios —ese manejador y la reconstruccion de un enlace compartido—
  y dos copias de una tabla es una que se queda atras.
*/
const DURACION_MS = {
  '2 hours': 2 * 60 * 60 * 1000,
  '8 hours': 8 * 60 * 60 * 1000,
  '1 day': 24 * 60 * 60 * 1000,
  '3 days': 3 * 24 * 60 * 60 * 1000
}

/**
 * Construye el registro de un escudo a partir del instante en que empieza.
 *
 * Las cuatro cadenas de hora son formato, no dato: se derivan del mismo instante,
 * dos en UTC y dos en la zona de quien mira. Por eso el enlace compartido lleva el
 * instante en ISO y no las cadenas —asi quien lo abre ve las horas en SU zona, que
 * es lo que necesita, en vez de las del que lo mando.
 */
const construirEscudo = (realmNumber, shieldName, inicio, shieldTime) => {
  const fin = new Date(inicio.getTime() + (DURACION_MS[shieldTime] ?? 0))

  return {
    realmNumber,
    shieldName,
    serverTime: inicio.toLocaleString('en-GB', { timeZone: 'UTC' }),
    shieldTime,
    localTime: inicio.toLocaleString('en-GB'),
    endServerTime: fin.toLocaleString('en-GB', { timeZone: 'UTC' }),
    endLocalTime: fin.toLocaleString('en-GB'),
    inicio: inicio.toISOString()
  }
}

/*
  Lo que viaja: por cada escudo, el reino, el nombre, el instante de inicio y la
  duracion. Nada de las horas ya formateadas.
*/
const aEstadoMinimo = lista =>
  (lista ?? [])
    .filter(s => s.inicio)
    .map(s => [s.realmNumber, s.shieldName, s.inicio, s.shieldTime])

const desdeEstadoMinimo = minimo => {
  if (!Array.isArray(minimo) || !minimo.length) return null

  const lista = minimo
    .map(x => {
      if (!Array.isArray(x) || x.length !== 4) return null
      const inicio = new Date(x[2])
      if (Number.isNaN(inicio.getTime()) || !DURACION_MS[x[3]]) return null

      return construirEscudo(String(x[0] ?? ''), String(x[1] ?? ''), inicio, x[3])
    })
    .filter(Boolean)

  return lista.length ? lista : null
}

// ** Styled Components
const CustomBox = styled(Box)(({ theme }) => ({
  marginTop: '4px',
  display: 'flex',
  justifyContent: 'flex-start',
  alignItems: 'center',
  flexDirection: 'row'
}))

const GreenButton = styled(Button)(({ theme }) => ({
  // Verde oscuro y texto blanco: #E8F1F5 sobre #1B8A6B daba 3,74:1, por debajo de AA.
  backgroundColor: theme.palette.customColors.skyPaletteTertiaryDark,
  color: theme.palette.common.white,
  '&:hover': {
    backgroundColor: theme.palette.customColors.skyPaletteTertiaryDark,
    filter: 'brightness(0.85)'
  }
}))

// ** Main Component
const ShieldsTracker = () => {
  // ** Hooks
  const theme = useTheme()

  // ** State Variables
  const [realmNumber, setRealmNumber] = useState('')
  const [shieldName, setShieldName] = useState('')
  const [serverTime, setServerTime] = useState('')
  const [shieldTime, setShieldTime] = useState('')
  const [shields, setShields] = useState([])
  const [isLoading, setIsLoading] = useState(true)

  // ** Load Saved Shields on Mount
  useEffect(() => {
    // El enlace compartido gana sobre lo guardado; lo propio sigue en localStorage
    const compartido = desdeEstadoMinimo(leerEstadoDeUrl())
    if (compartido) {
      setShields(compartido)
      setIsLoading(false)

      return
    }

    const savedShields = JSON.parse(localStorage.getItem('shields')) || []

    if (savedShields.length > 0) {
      const latestShield = savedShields[savedShields.length - 1]
      setRealmNumber(latestShield.realmNumber || '')
      setShieldName(latestShield.shieldName || '')
      setServerTime(latestShield.serverTime || '')
      setShieldTime(latestShield.shieldTime || '')
    }

    setShields(savedShields)
    setIsLoading(false)
  }, [])

  const handleShare = async () => {
    const minimo = aEstadoMinimo(shields)
    if (!minimo.length) return toast.error('Nothing to share yet — save a shield first.')

    const enlace = construirEnlace(minimo)
    if (!enlace) return toast.error('Could not build the link.')

    try {
      await navigator.clipboard.writeText(enlace)

      return toast.success('Link copied — paste it anywhere.')
    } catch (e) {
      window.history.replaceState(null, '', enlace)

      return toast.success('Link is now in the address bar — copy it from there.')
    }
  }

  // ** Save Shield Data
  const handleSaveData = () => {
    const existingShields = JSON.parse(localStorage.getItem('shields')) || []

    // Input is the user's local time (datetime-local gives local time)
    const newShield = construirEscudo(realmNumber, shieldName, new Date(serverTime), shieldTime)

    const updatedShields = [...existingShields, newShield]

    localStorage.setItem('shields', JSON.stringify(updatedShields))
    setShields(updatedShields)

    toast.success('New shield data successfully added to the browser!')
  }

  // ** Reset Shield Data
  const handleResetData = () => {
    localStorage.removeItem('shields')
    setShields([])

    setRealmNumber('')
    setShieldName('')
    setServerTime('')
    setShieldTime('')

    toast.success('All shield data has been cleared!')
  }

  // ** Render
  return (
    <>
      {/* Header Card */}
      <Card
        sx={{
          padding: '1rem 2rem',
          borderRadius: '12px',
          boxShadow: '0px 4px 10px rgba(0, 0, 0, 0.1)',
          marginBottom: '2rem',
          borderTop: '6px solid',
          borderTopColor: 'primary.main'
        }}
      >
        <CustomHeader icon='shields' title='SHIELDS' />
        <Divider sx={{ marginY: '1rem' }} />
        <Typography variant='h6' component='h2' sx={{ fontWeight: 'bold', marginBottom: '1rem', color: theme.palette.primary.main }}>
          Manage and Track Shield Timings
        </Typography>
        <Typography variant='body2' sx={{ marginBottom: 1, color: theme.palette.text.secondary }}>
          Use this tool to track and manage shield timings across realms. Record server times, specify shield durations,
          and save your data for future reference.
        </Typography>
        <Typography variant='body2' sx={{ marginBottom: 1, color: theme.palette.text.secondary }}>
          Shields are critical in War and Order — an expired shield during an active war means your city is
          vulnerable to attack. This tracker lets you record the exact server time your shield expires for each
          realm, so you always know when to renew protection before going offline.
        </Typography>
        <Typography variant='body2' sx={{ marginBottom: '1rem', color: theme.palette.text.secondary }}>
          Add multiple realms, choose your shield duration (3 hours, 8 hours, 1 day, 3 days, or 7 days), and save
          your schedule. All data is stored locally in your browser — nothing is sent to any server.
        </Typography>
        <Alert
          severity='warning'
        >
          {`After each modification (add, edit or delete), please use the `}
          <span role='img' aria-label='save' style={{ color: '#ffffff' }}>
            💾
          </span>{' '}
          {`button before refreshing or leaving the page.`}
        </Alert>
        <Typography
          variant='caption'
          sx={{
            display: 'block',
            textAlign: 'right',
            marginTop: '1rem',
            // text.secondary: con disabled este credito daba 2,1:1 sobre blanco
  color: theme.palette.text.secondary,
            fontSize: '0.75rem'
          }}
        >
          Created by DvdVgt
        </Typography>
      </Card>

      {/* Form Card */}
      <Card
        sx={{
          padding: '2rem',
          borderRadius: '12px',
          boxShadow: '0px 4px 10px rgba(0, 0, 0, 0.1)',
          backgroundColor: theme.palette.background.paper,
          marginBottom: '2rem'
        }}
      >
        <Box sx={{ flexGrow: 1 }}>
          <Grid container spacing={3}>
            <Grid size={{ xs: 12 }}>
              <Typography variant='h5' component='p' sx={{ fontWeight: 'bold', marginBottom: '1rem' }}>
                Shield Details
              </Typography>
              <Divider sx={{ marginBottom: '1rem' }} />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <CustomBox>
                <Typography variant='body2' color='primary' sx={{ minWidth: '120px' }}>
                  Realm:
                </Typography>
                <TextField
                  slotProps={{ htmlInput: { 'aria-label': 'Realm' } }}
                  id='realm-number'
                  size='small'
                  variant='outlined'
                  type='number'
                  value={realmNumber}
                  onChange={e => setRealmNumber(e.target.value)}
                  fullWidth
                />
              </CustomBox>
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <CustomBox>
                <Typography variant='body2' color='primary' sx={{ minWidth: '120px' }}>
                  Name:
                </Typography>
                <TextField
                  slotProps={{ htmlInput: { 'aria-label': 'Name' } }}
                  id='shield-name'
                  size='small'
                  variant='outlined'
                  type='text'
                  value={shieldName}
                  onChange={e => setShieldName(e.target.value)}
                  fullWidth
                />
              </CustomBox>
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <CustomBox>
                <Typography variant='body2' color='primary' sx={{ minWidth: '120px' }}>
                  Local Time:
                </Typography>
                <TextField
                  slotProps={{ htmlInput: { 'aria-label': 'Local Time' } }}
                  id='server-time'
                  size='small'
                  variant='outlined'
                  type='datetime-local'
                  value={serverTime}
                  onChange={e => setServerTime(e.target.value)}
                  fullWidth
                />
              </CustomBox>
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <CustomBox>
                <Typography variant='body2' color='primary' sx={{ minWidth: '120px' }}>
                  Shield Duration:
                </Typography>
                <Select
                  id='shield-time'
                  // El nombre va por inputProps: puesto suelto, MUI lo deja en el div
                  // raiz, que no tiene rol, y axe lo rechaza (aria-prohibited-attr).
                  inputProps={{ 'aria-label': 'Shield Duration' }}
                  value={shieldTime}
                  onChange={e => setShieldTime(e.target.value)}
                  size='small'
                  variant='outlined'
                  fullWidth
                >
                  <MenuItem value='2 hours'>2 hours</MenuItem>
                  <MenuItem value='8 hours'>8 hours</MenuItem>
                  <MenuItem value='1 day'>1 day</MenuItem>
                  <MenuItem value='3 days'>3 days</MenuItem>
                </Select>
              </CustomBox>
            </Grid>
            <Grid size={{ xs: 12 }}>
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: '1rem', alignItems: 'center' }}>
                <GreenButton
                  variant='contained'
                  startIcon={<Icon icon='ic:baseline-save' />}
                  onClick={handleSaveData}
                >
                  SAVE
                </GreenButton>
                <Button variant='outlined' startIcon={<Icon icon='ic:baseline-link' />} onClick={handleShare}>
                  SHARE
                </Button>
                <Button
                  variant='contained'
                  color='error'
                  startIcon={<Icon icon='ic:baseline-delete' />}
                  onClick={handleResetData}
                >
                  DELETE
                </Button>
              </Box>
            </Grid>
          </Grid>
        </Box>
      </Card>

      {/* Display Saved Shields */}
      <Grid container spacing={3}>
        {shields.map((shield, index) => (
          <Grid size={{ xs: 12, md: 6, lg: 4 }} key={index}>
            <Card
              sx={{
                padding: '1.5rem',
                borderRadius: '12px',
                boxShadow: '0px 4px 10px rgba(0, 0, 0, 0.1)',
                backgroundColor: theme.palette.background.paper,
                borderTop: `6px solid ${theme.palette.warning.light}`
              }}
            >
              <Typography variant='h6' component='h2' sx={{ fontWeight: 'bold', marginBottom: '1rem' }}>
                Shield {index + 1}
              </Typography>
              <Typography variant='body2' sx={{ marginBottom: '0.5rem' }}>
                <strong>Realm:</strong> {shield.realmNumber}
              </Typography>
              <Typography variant='body2' sx={{ marginBottom: '0.5rem' }}>
                <strong>Name:</strong> {shield.shieldName}
              </Typography>
              <Typography variant='body2' sx={{ marginBottom: '0.5rem' }}>
                <strong>Server Time:</strong> {shield.serverTime}
              </Typography>
              <Typography variant='body2' sx={{ marginBottom: '0.5rem' }}>
                <strong>Local Time:</strong> {shield.localTime}
              </Typography>
              <Typography variant='body2' sx={{ marginBottom: '0.5rem' }}>
                <strong>Duration:</strong> {shield.shieldTime}
              </Typography>
              <Divider sx={{ marginY: '1rem' }} />
              <Typography variant='h7' sx={{ fontWeight: 'bold' }}>
                Endtime:
              </Typography>
              <Typography variant='body2' sx={{ marginBottom: '0.5rem', marginTop: '0.5rem' }}>
                <strong>Server Time:</strong> {shield.endServerTime}
              </Typography>
              <Typography variant='body2'>
                <strong>Local Time:</strong> {shield.endLocalTime}
              </Typography>
            </Card>
          </Grid>
        ))}
      </Grid>
    </>
  )
}

ShieldsTracker.noLoadingGate = true

export default ShieldsTracker
